// dashboard/frontend/src/pages/DiscoveryPage.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import NavRail from '../components/NavRail';
import RuleDrillPanel from '../components/RuleDrillPanel';
import ConfigShadowSection from '../components/ConfigShadowSection';
import ArmingConsole from '../components/ArmingConsole';
import { getDiscoveryRuns, getDiscoveryRules, getRuleActivations, postRunBacktestAll, putRuleActivation, putRuleMode } from '../services/api';
import usePersistedState from '../hooks/usePersistedState';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{--bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;}
.disc *{box-sizing:border-box;margin:0;padding:0}
.disc,.disc *{font-family:'JetBrains Mono',ui-monospace,monospace;}
.disc{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.disc .sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.disc .sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.disc .sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.disc .sb-item:hover{background:var(--surface);color:var(--text);}
.disc .sb-item.on{background:var(--surface);color:var(--cyan);box-shadow:inset 2px 0 0 var(--cyan);}
.disc .sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.disc .sb-item:hover .tip{opacity:1;}
.disc .sb-spacer{flex:1;}
.disc .main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.disc .page{flex:1;overflow:auto;padding:16px;}

/* page-specific */
.disc .disc-meta{font-size:12px;color:var(--text2);margin-bottom:10px;}
.disc .disc-meta b{color:var(--text);}
.disc .disc-row{cursor:pointer;}
.disc .disc-row:hover{background:var(--surface2);}
.disc table.disc-tbl{width:100%;border-collapse:collapse;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.disc table.disc-tbl th{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 10px;background:#0e1014;font-weight:600;}
.disc table.disc-tbl td{padding:8px 10px;font-size:11.5px;border-top:1px solid #16191f;vertical-align:middle;}
.disc .h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-bottom:12px;}

/* sort/ctrl row */
.disc .sort-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px;}
.disc .sort-lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.disc .sort-row select{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:6px 10px;font-family:inherit;font-size:12px;cursor:pointer;min-width:260px;}
.disc .sort-row select:focus{border-color:var(--cyan);outline:none;}
.disc .sort-row select option{background:var(--surface2);color:var(--text);}

/* filter row */
.disc .filter-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:12px;}
.disc .filter-row input[type="number"]{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:5px 8px;font-family:inherit;font-size:11px;width:90px;}
.disc .filter-row input[type="number"]:focus{border-color:var(--cyan);outline:none;}
.disc .filter-row label{display:flex;align-items:center;gap:5px;font-size:11px;color:var(--text2);cursor:pointer;}
.disc .filter-row input[type="checkbox"]{accent-color:var(--cyan);width:13px;height:13px;cursor:pointer;}

/* toast */
.disc .batch-toast{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:10px 14px;font-size:12px;margin-bottom:10px;color:var(--text2);}
.disc .batch-toast b{color:var(--text);}
.disc .batch-toast.fresh{border-color:var(--cyan);color:var(--cyan);}

/* tdp overlay + panel (RuleDrillPanel + CohortBacktestSection + BacktestMatrixView) */
.tdp-overlay{position:fixed;inset:0;background:rgba(0,0,0,.66);backdrop-filter:blur(2px);z-index:200;display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:24px;font-family:'JetBrains Mono',ui-monospace,monospace;color:var(--text);}
.tdp-panel{background:var(--bg);border:1px solid var(--border);border-radius:14px;width:100%;max-width:min(1200px,95vw);max-height:90vh;overflow-y:auto;padding:20px;box-shadow:0 24px 70px rgba(0,0,0,.6);}
.tdp-head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;}
.tdp-title{font-size:15px;font-weight:700;}
.tdp-sub{font-size:11px;color:var(--text2);}
.tdp-ctrl{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:8px 0;}
.tdp-ctrl .lbl{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-right:2px;}
.tdp-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 10px;border-radius:6px;cursor:pointer;letter-spacing:.03em;}
.tdp-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.tdp-btn:disabled{opacity:.45;cursor:not-allowed;}
.tdp-empty{padding:24px;text-align:center;color:var(--text2);font-size:12px;}
.tdp-bt-card{margin-top:12px;border:1px solid var(--border);border-radius:8px;padding:12px;background:var(--surface);}
.tdp-bt-hd{font-size:13px;font-weight:600;margin-bottom:8px;color:var(--text);}
.tdp-score{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:14px;}
@media(max-width:680px){.tdp-score{grid-template-columns:repeat(3,1fr);}}
.tdp-kpi{background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:9px 10px;}
.tdp-kpi .k{font-size:9px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;}
.tdp-kpi .v{font-size:16px;font-weight:800;margin-top:3px;}
.tdp-tbl{width:100%;border-collapse:collapse;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.tdp-tbl th{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 10px;background:#0e1014;}
.tdp-tbl th.num{text-align:right;}
.tdp-tbl td{padding:8px 10px;font-size:11.5px;border-top:1px solid #16191f;}
.tdp-tbl td.num{text-align:right;font-variant-numeric:tabular-nums;}
.tdp-mint{font-size:10px;color:var(--text2);cursor:pointer;}
.tdp-tl{font-size:9px;font-weight:700;text-decoration:none;padding:2px 5px;border-radius:4px;margin-right:4px;}
.tdp-tl.sol{color:#9bd1ff;} .tdp-tl.gm{color:var(--amber);}
.tdp-pos{color:var(--green);} .tdp-neg{color:var(--red);} .tdp-muted{color:var(--text2);}
.tdp-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:5px;}
.tdp-badge.rug{background:rgba(255,59,92,.16);color:var(--red);}
.disc .cs-wrap{margin-top:6px;}
.disc .cs-caveat{font-size:11px;color:var(--amber);margin-bottom:10px;}
.disc .cs-err{color:var(--red);font-size:12px;margin-bottom:8px;}
.disc .cs-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:5px;text-transform:uppercase;}
.disc .cs-badge[data-ev="live"]{background:rgba(0,224,138,.16);color:var(--green);}
.disc .cs-badge[data-ev="batch"]{background:rgba(255,176,32,.16);color:var(--amber);}
.disc .cs-badge[data-arm="buy"]{background:rgba(255,59,92,.16);color:var(--red);}
.disc .cs-badge[data-arm="observe"]{background:rgba(34,211,238,.16);color:var(--cyan);}
.disc .cs-badge[data-arm="off"]{background:rgba(138,143,152,.16);color:var(--text2);}
.disc .cs-warn,.disc .cs-drift{color:var(--amber);margin-left:4px;cursor:help;}
.disc .cs-on{color:var(--green);} .disc .cs-slot{color:var(--text2);font-size:10px;}
.disc .cs-cnt{color:var(--cyan);font-size:10px;}
.disc .cs-tabs{display:flex;gap:6px;margin-bottom:12px;}
.disc .arm-bar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;}
.disc .arm-in{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:6px 10px;font-family:inherit;font-size:12px;}
`;

const SORTS = [
  { k: 'lift_oos', label: 'Lift OOS' },
  { k: 'lift_train', label: 'Lift train' },
  { k: 'support_train', label: 'Support' },
  { k: 'feature', label: 'Feature' },
  { k: 'ev_median_pct', label: 'EV med' },
  { k: 'win_rate_pct', label: 'WR%' },
  { k: 'sol_pnl_total', label: 'SOL PnL' },
  { k: 'median_peak_x', label: 'Peak méd' },
  { k: 'rug_rate_pct', label: 'Rug%' },
];

// Copie du set canonique scripts/lib/rule_eval.py. Phase 1b : + 4 create-tx (parité 99%).
const LIVE_FEATURES = new Set([
  'snap_price', 'token_age_sec', 'progress',
  'real_liquidity_sol', 'virtual_liquidity_sol', 'unique_wallets_30s',
  'sol_buy_in_create_tx', 'pct_supply_create_tx',
  'n_atomic_buyers_create_tx', 'n_buyers_noncreator_create_tx',
]);
const evalOf = (f) => (LIVE_FEATURES.has(f) ? 'live' : 'batch');

const predKey = (feature, op, threshold, slotTag) => `${feature}|${op}|${threshold}|${slotTag || ''}`;

const BATCH_WARN =
  'Couverture rétrospective (look-ahead). La feature (slot-0/bundle/holders) n’est PAS connue ' +
  'à l’instant de la décision live — reconstruite par cron offline a posteriori. La couverture ' +
  'batch n’est PAS une preuve d’exécutabilité : à valider en #4. Ne pas confondre ' +
  '"bonne couverture batch" avec "prête pour le live".';
const LIVE_NOTE =
  'Feature disponible à l’instant de la décision → un would_buy live prouve l’exécutabilité.';

// durée refetch auto après un batch (ms)
const BATCH_REFETCH_DURATION_MS = 2 * 60 * 1000;
const BATCH_REFETCH_INTERVAL_MS = 8000;

export default function DiscoveryPage() {
  const qc = useQueryClient();

  const [runId, setRunId] = useState(null);
  const [sort, setSort] = usePersistedState('discovery.sort', 'lift_oos');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('rules'); // 'rules' | 'config' | 'arming'

  // filtres perf (persistés)
  const [minEvMedian, setMinEvMedian] = usePersistedState('discovery.minEvMedian', '');
  const [minWr, setMinWr] = usePersistedState('discovery.minWr', '');
  const [minSolPnl, setMinSolPnl] = usePersistedState('discovery.minSolPnl', '');
  const [onlyBacktested, setOnlyBacktested] = usePersistedState('discovery.onlyBacktested', false);
  const [minPeakMedian, setMinPeakMedian] = usePersistedState('discovery.minPeakMedian', '');
  const [maxRug, setMaxRug] = usePersistedState('discovery.maxRug', '');
  const [evalFilter, setEvalFilter] = usePersistedState('discovery.evalFilter', '');
  const [armFilter, setArmFilter] = usePersistedState('discovery.armFilter', '');
  const [armErr, setArmErr] = useState(null);

  // refetch auto post-batch
  const [batchActive, setBatchActive] = useState(false);
  const [batchTally, setBatchTally] = useState(null);
  const batchTimerRef = useRef(null);

  // nettoyage du timer à l'unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    };
  }, []);

  const runsQ = useQuery({ queryKey: ['disc-runs'], queryFn: getDiscoveryRuns });
  const rulesQ = useQuery({
    queryKey: ['disc-rules', runId, sort, minEvMedian, minWr, minSolPnl, onlyBacktested, minPeakMedian, maxRug],
    queryFn: () => getDiscoveryRules({
      runId,
      sort,
      // Rug% : plus bas = mieux → tri ascendant ; toutes les autres clés = desc.
      order: sort === 'rug_rate_pct' ? 'asc' : 'desc',
      minEvMedian: minEvMedian !== '' ? minEvMedian : undefined,
      minWr: minWr !== '' ? minWr : undefined,
      minSolPnl: minSolPnl !== '' ? minSolPnl : undefined,
      onlyBacktested: onlyBacktested || undefined,
      minPeakMedian: minPeakMedian !== '' ? minPeakMedian : undefined,
      maxRug: maxRug !== '' ? maxRug : undefined,
    }),
    refetchInterval: batchActive ? BATCH_REFETCH_INTERVAL_MS : false,
  });

  // Résolution du run réel : runId=null = "(dernier)" sélectionné ; on lit l'id depuis la réponse.
  const effectiveRunId = runId || rulesQ.data?.run?.run_id || null;

  const batchMut = useMutation({
    mutationFn: () => postRunBacktestAll(effectiveRunId),
    onSuccess: (data) => {
      setBatchTally(data);
      setBatchActive(true);
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
      batchTimerRef.current = setTimeout(() => {
        setBatchActive(false);
        batchTimerRef.current = null;
      }, BATCH_REFETCH_DURATION_MS);
      qc.invalidateQueries({ queryKey: ['disc-rules'] });
    },
  });

  const actQ = useQuery({ queryKey: ['disc-activations'], queryFn: getRuleActivations });
  const actByPred = useMemo(() => {
    const m = new Map();
    (actQ.data?.activations || []).forEach((a) => m.set(predKey(a.feature, a.op, a.threshold, a.slot_tag), a));
    return m;
  }, [actQ.data]);
  const armStateOf = (r) => {
    const a = actByPred.get(predKey(r.feature, r.op, r.threshold, r.slot_tag));
    if (!a) return 'none';
    if (a.active && a.mode === 'buy') return 'buy';
    if (a.active) return 'observe';
    return 'off';
  };

  const armMut = useMutation({
    mutationFn: async (r) => {
      const a = actByPred.get(predKey(r.feature, r.op, r.threshold, r.slot_tag));
      if (a && a.active) return putRuleMode(a.id, 'buy');
      // création (ou réactivation) — préserve la config existante, l'UPSERT écrase sinon
      const act = await putRuleActivation({
        feature: r.feature, op: r.op, threshold: r.threshold, slot_tag: r.slot_tag,
        label_config: a?.label_config ?? r.label_config,
        source_run_id: a?.source_run_id ?? r.run_id,
        size_mult: a?.size_mult ?? null, size_cap_sol: a?.size_cap_sol ?? null,
        note: a?.note ?? null, active: true,
      });
      return putRuleMode(act.id, 'buy');
    },
    onSuccess: () => { setArmErr(null); qc.invalidateQueries({ queryKey: ['disc-activations'] }); },
    onError: (e) => setArmErr(e?.response?.data?.detail || e.message),
  });
  const disarmMut = useMutation({
    mutationFn: (id) => putRuleMode(id, 'observe'),
    onSuccess: () => { setArmErr(null); qc.invalidateQueries({ queryKey: ['disc-activations'] }); },
    onError: (e) => setArmErr(e?.response?.data?.detail || e.message),
  });

  const run = rulesQ.data?.run;
  const rules = rulesQ.data?.rules || [];
  const shownRules = rules
    .filter(r => !evalFilter || evalOf(r.feature) === evalFilter)
    .filter(r => !armFilter || (armFilter === 'none' ? ['none', 'off'].includes(armStateOf(r)) : armStateOf(r) === armFilter));
  const runs = runsQ.data?.runs || [];

  return (
    <div className="disc">
      <style>{CSS}</style>
      <NavRail />
      <div className="main">
        <div className="page">
          <div className="h1">Découverte — règles minées</div>
          <div className="cs-tabs">
            <button className={`tdp-btn ${view === 'rules' ? 'on' : ''}`} onClick={() => setView('rules')}>Règles</button>
            <button className={`tdp-btn ${view === 'config' ? 'on' : ''}`} onClick={() => setView('config')}>Config &amp; Shadow</button>
            <button className={`tdp-btn ${view === 'arming' ? 'on' : ''}`} onClick={() => setView('arming')}>Armement</button>
          </div>

          {view === 'rules' && (
            <>
              {/* Ligne 1 : Run + tris */}
              <div className="sort-row">
                <span className="sort-lbl">Run</span>
                <select value={runId || ''} onChange={(e) => setRunId(e.target.value || null)}>
                  <option value="">(dernier)</option>
                  {runs.map(r => (
                    <option key={r.run_id} value={r.run_id}>
                      {r.mined_at} · {r.population} · {r.n_rules} règles
                    </option>
                  ))}
                </select>
                <span className="sort-lbl">Tri</span>
                {SORTS.map(s => (
                  <button key={s.k} className={`tdp-btn ${sort === s.k ? 'on' : ''}`} onClick={() => setSort(s.k)}>{s.label}</button>
                ))}
              </div>

              {/* Ligne 2 : filtres perf (presets) + bouton batch */}
              <div className="filter-row">
                <span className="sort-lbl">Filtres</span>

                {/* EV med */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">EV med</span>
                  {[['Tous', ''], ['≥0', '0'], ['≥10', '10'], ['≥20', '20']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${minEvMedian === val ? 'on' : ''}`} onClick={() => setMinEvMedian(val)}>{lbl}</button>
                  ))}
                  <input
                    type="number"
                    value={minEvMedian}
                    onChange={e => setMinEvMedian(e.target.value)}
                    placeholder="min %"
                    style={{ width: 70, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, padding: '5px 8px', borderRadius: 6, outline: 'none' }}
                  />
                </span>

                {/* WR */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">WR</span>
                  {[['Tous', ''], ['≥50', '50'], ['≥60', '60']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${minWr === val ? 'on' : ''}`} onClick={() => setMinWr(val)}>{lbl}</button>
                  ))}
                  <input
                    type="number"
                    value={minWr}
                    onChange={e => setMinWr(e.target.value)}
                    placeholder="min %"
                    style={{ width: 70, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, padding: '5px 8px', borderRadius: 6, outline: 'none' }}
                  />
                </span>

                {/* SOL PnL */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">SOL PnL</span>
                  {[['Tous', ''], ['≥0', '0'], ['≥0.05', '0.05']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${minSolPnl === val ? 'on' : ''}`} onClick={() => setMinSolPnl(val)}>{lbl}</button>
                  ))}
                  <input
                    type="number"
                    value={minSolPnl}
                    onChange={e => setMinSolPnl(e.target.value)}
                    placeholder="min SOL"
                    style={{ width: 70, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, padding: '5px 8px', borderRadius: 6, outline: 'none' }}
                  />
                </span>

                {/* Peak méd */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">Peak méd</span>
                  {[['Tous', ''], ['≥1.5×', '1.5'], ['≥2×', '2']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${minPeakMedian === val ? 'on' : ''}`} onClick={() => setMinPeakMedian(val)}>{lbl}</button>
                  ))}
                  <input
                    type="number"
                    value={minPeakMedian}
                    onChange={e => setMinPeakMedian(e.target.value)}
                    placeholder="min ×"
                    style={{ width: 70, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, padding: '5px 8px', borderRadius: 6, outline: 'none' }}
                  />
                </span>

                {/* Rug max */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">Rug max</span>
                  {[['Tous', ''], ['≤25', '25'], ['≤50', '50']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${maxRug === val ? 'on' : ''}`} onClick={() => setMaxRug(val)}>{lbl}</button>
                  ))}
                  <input
                    type="number"
                    value={maxRug}
                    onChange={e => setMaxRug(e.target.value)}
                    placeholder="max %"
                    style={{ width: 70, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, padding: '5px 8px', borderRadius: 6, outline: 'none' }}
                  />
                </span>

                {/* Éval */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">Éval</span>
                  {[['Tous', ''], ['Live', 'live'], ['Batch', 'batch']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${evalFilter === val ? 'on' : ''}`} onClick={() => setEvalFilter(val)}>{lbl}</button>
                  ))}
                </span>

                {/* Arm. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="sort-lbl">Arm.</span>
                  {[['Tous', ''], ['Armées', 'buy'], ['Observées', 'observe'], ['Sans', 'none']].map(([lbl, val]) => (
                    <button key={val} className={`tdp-btn ${armFilter === val ? 'on' : ''}`} onClick={() => setArmFilter(val)}>{lbl}</button>
                  ))}
                </span>

                {/* Backtestées */}
                <button
                  className={`tdp-btn ${onlyBacktested ? 'on' : ''}`}
                  onClick={() => setOnlyBacktested(v => !v)}
                >Backtestées seulement</button>

                <button
                  className="tdp-btn"
                  style={{ marginLeft: 'auto', borderColor: 'var(--amber)', color: 'var(--amber)' }}
                  onClick={() => batchMut.mutate()}
                  disabled={!effectiveRunId || batchMut.isPending}
                  title="Lance un backtest canonique sur toutes les règles du run"
                >
                  {batchMut.isPending ? 'Lancement…' : 'Backtester tout le run'}
                </button>
              </div>

              {/* Toast tally */}
              {batchTally && (
                <div className={`batch-toast ${batchActive ? 'fresh' : ''}`}>
                  {batchActive && <span style={{ color: 'var(--cyan)', marginRight: 6 }}>&#9654; refetch auto actif</span>}
                  enqueued <b>{batchTally.enqueued}</b>
                  {batchTally.skipped_fresh != null && <> · fresh <b>{batchTally.skipped_fresh}</b></>}
                  {batchTally.skipped_empty != null && <> · vide <b>{batchTally.skipped_empty}</b></>}
                  {batchTally.skipped_invalid != null && <> · invalide <b>{batchTally.skipped_invalid}</b></>}
                  {batchTally.total != null && <> / total <b>{batchTally.total}</b></>}
                </div>
              )}
              {batchMut.isError && (
                <div className="batch-toast" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
                  Erreur batch : {batchMut.error?.message}
                </div>
              )}

              {run && (
                <div className="disc-meta">
                  population <b>{run.population}</b> · dataset {run.dataset_rows} lignes ·
                  bundle coverage {run.bundle_coverage ?? '—'} ·
                  ML {run.ml_importances?.n_train ? `n_train=${run.ml_importances.n_train}` : '—'}
                  <div style={{ color: 'var(--amber)' }}>
                    Lift OOS = signal relatif (held-out). Niveaux absolus indicatifs ; verdict PnL = backtest cohorte.
                  </div>
                </div>
              )}

              {armErr && <div className="cs-err">Erreur armement : {armErr}</div>}

              {rulesQ.isLoading && <div className="tdp-empty">Chargement…</div>}
              {rulesQ.isError && <div className="tdp-empty">Erreur : {rulesQ.error?.message}</div>}
              {!rulesQ.isLoading && rules.length === 0 && <div className="tdp-empty">Aucune règle (lancer un mining).</div>}
              {!rulesQ.isLoading && rules.length > 0 && shownRules.length === 0 && (
                <div className="tdp-empty">Aucune règle avec ces filtres.</div>
              )}

              {shownRules.length > 0 && (
                <table className="disc-tbl">
                  <thead>
                    <tr>
                      <th>Feature</th><th>Op</th><th>Seuil</th><th>slot</th><th>Éval.</th><th>Arm.</th>
                      <th>Lift OOS</th><th>Lift train</th><th>Support</th><th>Label</th>
                      <th>EV med</th><th>WR%</th><th>Peak méd</th><th>Rug%</th><th>SOL PnL</th><th>N</th><th>best TP</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRules.map(r => {
                      const ev = evalOf(r.feature);
                      const arm = armStateOf(r);
                      return (
                      <tr key={r.id} className="disc-row" onClick={() => setSelected(r)}>
                        <td>{r.feature}</td><td>{r.op}</td><td>{r.threshold}</td><td>{r.slot_tag || '—'}</td>
                        <td>
                          <span className="cs-badge" data-ev={ev} title={ev === 'batch' ? BATCH_WARN : LIVE_NOTE}>{ev}</span>
                        </td>
                        <td>
                          {arm === 'none' ? <span className="cs-slot">—</span> : (
                            <span className="cs-badge" data-arm={arm}
                              title={arm === 'buy' ? 'Activation ARMÉE (mode buy) — achète pour de vrai si master ON.'
                                : arm === 'observe' ? 'Activation en shadow (observe) — ne trade pas.'
                                : 'Activation désactivée.'}>
                              {arm === 'buy' ? 'armée' : arm === 'observe' ? 'observe' : 'désact.'}
                            </span>
                          )}
                        </td>
                        <td><b>{fmt(r.lift_oos)}</b></td><td>{fmt(r.lift_train)}</td><td>{r.support_train ?? '—'}</td>
                        <td>{r.label_config ? `≥${r.label_config.peak_threshold_pct}%/${r.label_config.basis}` : '—'}</td>
                        <td style={{ color: r.bt != null ? (r.bt.ev_median_pct > 0 ? 'var(--green)' : 'var(--red)') : 'var(--text2)' }}>
                          {r.bt != null ? fmtEv(r.bt.ev_median_pct) : '—'}
                        </td>
                        <td>{r.bt != null ? fmtWr(r.bt.win_rate_pct) : '—'}</td>
                        <td>{r.bt != null && r.bt.median_peak_x != null ? fmtPeak(r.bt.median_peak_x) : '—'}</td>
                        <td>{r.bt != null && r.bt.rug_rate_pct != null ? fmtRug(r.bt.rug_rate_pct) : '—'}</td>
                        <td style={{ color: r.bt ? (r.bt.sol_pnl_total > 0 ? 'var(--green)' : 'var(--red)') : 'var(--text2)' }}>
                          {r.bt != null ? fmtSol(r.bt.sol_pnl_total) : '—'}
                        </td>
                        <td style={{ color: 'var(--text2)', fontSize: '10px' }}>
                          {r.bt != null ? `${r.bt.n_valid}/${r.bt.n_picks}` : '—'}
                        </td>
                        <td style={{ color: 'var(--cyan)' }}>
                          {r.bt != null ? `${r.bt.tp_pct}%` : '—'}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {arm === 'buy' ? (
                            <button className="tdp-btn"
                              disabled={disarmMut.isPending}
                              onClick={() => {
                                const a = actByPred.get(predKey(r.feature, r.op, r.threshold, r.slot_tag));
                                if (a) disarmMut.mutate(a.id);
                              }}>→ observe</button>
                          ) : (
                            <button className="tdp-btn" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                              disabled={ev !== 'live' || armMut.isPending}
                              title={ev !== 'live' ? 'Règle batch non-armable (feature inconnue à la décision live)'
                                : 'Crée/active l\'activation et passe en mode buy — ACHAT RÉEL si master ON'}
                              onClick={() => {
                                if (window.confirm(`ARMER ${r.feature} ${r.op} ${r.threshold} ?\nAchat RÉEL si master armé (MODE live).`)) armMut.mutate(r);
                              }}>armer</button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {selected && <RuleDrillPanel rule={selected} onClose={() => setSelected(null)} />}
            </>
          )}

          {view === 'config' && <ConfigShadowSection runId={runId} />}
          {view === 'arming' && <ArmingConsole runId={runId} />}
        </div>
      </div>
    </div>
  );
}

function fmt(v) {
  return (v === null || v === undefined) ? '—' : Number(v).toFixed(2);
}

function fmtEv(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
}

function fmtWr(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(1) + '%';
}

function fmtSol(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return (n >= 0 ? '+' : '') + n.toFixed(4);
}

function fmtPeak(v) {
  return Number(v).toFixed(2) + '×';
}

function fmtRug(v) {
  return Number(v).toFixed(1) + '%';
}
